import React, { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import api, { professionalsAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ScrollArea } from '@/components/ui/scroll-area';
import ImageUpload from '@/components/ImageUpload';
import AddressAutocomplete from '@/components/AddressAutocomplete';
import {
  User,
  Stethoscope,
  MapPin,
  Calendar,
  Clock,
  Video,
  Home,
  CreditCard,
  Loader2,
  Save,
  Plus,
  X,
  GraduationCap,
  Award,
  Building2,
  Languages,
  Phone,
  Mail,
  Globe,
  Shield,
  CheckCircle
} from 'lucide-react';
import { toast } from 'sonner';

const specialties = [
  { id: 'general', name: 'Médecine Générale' },
  { id: 'pediatric', name: 'Pédiatrie' },
  { id: 'gynecology', name: 'Gynécologie' },
  { id: 'cardiology', name: 'Cardiologie' },
  { id: 'dermatology', name: 'Dermatologie' },
  { id: 'ophthalmology', name: 'Ophtalmologie' },
  { id: 'dentistry', name: 'Dentiste' },
  { id: 'physiotherapy', name: 'Kinésithérapie' },
  { id: 'nursing', name: 'Infirmier(e)' },
  { id: 'midwife', name: 'Sage-femme' },
  { id: 'psychology', name: 'Psychologie' },
  { id: 'nutrition', name: 'Nutrition' },
  { id: 'neurology', name: 'Neurologie' },
  { id: 'psychiatry', name: 'Psychiatrie' },
  { id: 'orthopedics', name: 'Orthopédie' },
  { id: 'urology', name: 'Urologie' },
  { id: 'ent', name: 'ORL' },
];

const titles = ['Dr.', 'Pr.', 'M.', 'Mme', 'Mlle'];

const languageOptions = ['Français', 'Anglais', 'Espagnol', 'Allemand', 'Arabe', 'Chinois', 'Ewondo', 'Douala', 'Fulfulde'];

const dayLabels = {
  monday: 'Lundi',
  tuesday: 'Mardi',
  wednesday: 'Mercredi',
  thursday: 'Jeudi',
  friday: 'Vendredi',
  saturday: 'Samedi',
  sunday: 'Dimanche'
};

const defaultSlots = {
  monday: [],
  tuesday: [],
  wednesday: [],
  thursday: [],
  friday: [],
  saturday: [],
  sunday: []
};

const ProfessionalProfile = () => {
  const { user: authUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('info');
  
  // Profile state
  const [profile, setProfile] = useState({
    title: '',
    specialty: '',
    professional_type: '',
    bio: '',
    description: '',
    license_number: '',
    experience_years: '',
    education: '',
    profile_image: '',
    cover_image: '',
    phone: '',
    email: '',
    website: '',
    address: '',
    city: 'Yaoundé',
    latitude: null,
    longitude: null,
    languages: ['Français'],
    certifications: [],
    affiliations: [],
    achievements: [],
    services: [],
    consultation_fee: '',
    video_fee: '',
    home_visit_fee: '',
    consultation_duration: 30,
    video_duration: 20,
    accepts_new_patients: true,
    accepts_insurance: false,
    insurance_providers: [],
    consultation_slots: { ...defaultSlots },
    video_slots: { ...defaultSlots },
    home_visit_areas: [],
    home_visit_radius_km: 10,
  });
  
  // Temp inputs for lists
  const [newCertification, setNewCertification] = useState('');
  const [newAffiliation, setNewAffiliation] = useState('');
  const [newAchievement, setNewAchievement] = useState('');
  const [newLanguage, setNewLanguage] = useState('');
  const [newInsurance, setNewInsurance] = useState('');
  const [newArea, setNewArea] = useState('');

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      const response = await professionalsAPI.getProfile();
      if (response.data.profile) {
        setProfile(prev => ({
          ...prev,
          ...response.data.profile,
          consultation_slots: response.data.profile.consultation_slots || { ...defaultSlots },
          video_slots: response.data.profile.video_slots || { ...defaultSlots },
        }));
      }
    } catch (error) {
      console.error('Error loading profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const dataToSave = {
        ...profile,
        consultation_fee: parseFloat(profile.consultation_fee) || 0,
        video_fee: parseFloat(profile.video_fee) || 0,
        home_visit_fee: parseFloat(profile.home_visit_fee) || 0,
        experience_years: parseInt(profile.experience_years) || null,
        consultation_duration: parseInt(profile.consultation_duration) || 30,
        video_duration: parseInt(profile.video_duration) || 20,
        home_visit_radius_km: parseInt(profile.home_visit_radius_km) || 10,
      };
      
      await professionalsAPI.updateProfile(dataToSave);
      toast.success('Profil mis à jour avec succès');
    } catch (error) {
      console.error('Error saving profile:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const addToList = (listName, value, setValue) => {
    if (!value.trim()) return;
    if (!profile[listName].includes(value.trim())) {
      setProfile(prev => ({
        ...prev,
        [listName]: [...prev[listName], value.trim()]
      }));
    }
    setValue('');
  };

  const removeFromList = (listName, index) => {
    setProfile(prev => ({
      ...prev,
      [listName]: prev[listName].filter((_, i) => i !== index)
    }));
  };

  const handleServiceToggle = (service) => {
    setProfile(prev => ({
      ...prev,
      services: prev.services.includes(service)
        ? prev.services.filter(s => s !== service)
        : [...prev.services, service]
    }));
  };

  const addTimeSlot = (slotType, day) => {
    const slotsKey = slotType === 'consultation' ? 'consultation_slots' : 'video_slots';
    const currentSlots = profile[slotsKey][day] || [];
    
    // Default new slot
    const newSlot = { start: '08:00', end: '12:00' };
    
    setProfile(prev => ({
      ...prev,
      [slotsKey]: {
        ...prev[slotsKey],
        [day]: [...currentSlots, newSlot]
      }
    }));
  };

  const updateTimeSlot = (slotType, day, index, field, value) => {
    const slotsKey = slotType === 'consultation' ? 'consultation_slots' : 'video_slots';
    
    setProfile(prev => ({
      ...prev,
      [slotsKey]: {
        ...prev[slotsKey],
        [day]: prev[slotsKey][day].map((slot, i) => 
          i === index ? { ...slot, [field]: value } : slot
        )
      }
    }));
  };

  const removeTimeSlot = (slotType, day, index) => {
    const slotsKey = slotType === 'consultation' ? 'consultation_slots' : 'video_slots';
    
    setProfile(prev => ({
      ...prev,
      [slotsKey]: {
        ...prev[slotsKey],
        [day]: prev[slotsKey][day].filter((_, i) => i !== index)
      }
    }));
  };

  const handlePhotoUpload = async (url, type) => {
    try {
      if (type === 'profile') {
        setProfile(prev => ({ ...prev, profile_image: url }));
      } else {
        setProfile(prev => ({ ...prev, cover_image: url }));
      }
      toast.success('Photo mise à jour');
    } catch (error) {
      toast.error('Erreur lors du téléchargement');
    }
  };

  const getLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setProfile(prev => ({
            ...prev,
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          }));
          toast.success('Localisation récupérée');
        },
        () => {
          toast.error('Impossible de récupérer la localisation');
        }
      );
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-red-500" />
      </div>
    );
  }

  return (
    <div className="section-padding py-6" data-testid="professional-profile-page">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans' }}>
              Mon Profil Professionnel
            </h1>
            <p className="text-slate-500 text-sm mt-1">
              Complétez votre profil pour attirer plus de patients
            </p>
          </div>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-red-500 hover:bg-red-600 text-white"
            data-testid="save-profile-btn"
          >
            {saving ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            Enregistrer
          </Button>
        </div>

        {/* Profile Completeness */}
        {!profile.specialty && (
          <Card className="mb-6 bg-amber-50 border-amber-200">
            <CardContent className="py-4">
              <div className="flex items-center gap-3">
                <Shield className="w-5 h-5 text-amber-600" />
                <p className="text-amber-800 text-sm">
                  Complétez votre profil pour être visible par les patients. 
                  Les champs marqués * sont obligatoires.
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          {/* Mobile: Scrollable horizontal tabs */}
          <div className="mb-6 -mx-4 px-4 overflow-x-auto scrollbar-hide">
            <TabsList className="inline-flex w-auto min-w-full sm:grid sm:grid-cols-5 gap-1 p-1">
              <TabsTrigger value="info" className="flex-shrink-0 px-3 py-2 text-xs sm:text-sm whitespace-nowrap">
                <User className="w-4 h-4 mr-1.5" />
                <span className="hidden xs:inline">Infos</span>
                <span className="xs:hidden">Info</span>
              </TabsTrigger>
              <TabsTrigger value="qualifications" className="flex-shrink-0 px-3 py-2 text-xs sm:text-sm whitespace-nowrap">
                <GraduationCap className="w-4 h-4 mr-1.5" />
                <span className="hidden sm:inline">Qualifications</span>
                <span className="sm:hidden">Diplômes</span>
              </TabsTrigger>
              <TabsTrigger value="services" className="flex-shrink-0 px-3 py-2 text-xs sm:text-sm whitespace-nowrap">
                <Stethoscope className="w-4 h-4 mr-1.5" />
                Services
              </TabsTrigger>
              <TabsTrigger value="availability" className="flex-shrink-0 px-3 py-2 text-xs sm:text-sm whitespace-nowrap">
                <Calendar className="w-4 h-4 mr-1.5" />
                <span className="hidden sm:inline">Disponibilités</span>
                <span className="sm:hidden">Dispo</span>
              </TabsTrigger>
              <TabsTrigger value="pricing" className="flex-shrink-0 px-3 py-2 text-xs sm:text-sm whitespace-nowrap">
                <CreditCard className="w-4 h-4 mr-1.5" />
                Tarifs
              </TabsTrigger>
            </TabsList>
          </div>

          <Card className="border-0 shadow-lg">
            <CardContent className="p-6">
              {/* Tab 1: Basic Info */}
              <TabsContent value="info" className="mt-0 space-y-6">
                {/* Photos */}
                <div className="flex flex-col md:flex-row gap-6">
                  <div className="flex flex-col items-center">
                    <Label className="mb-2">Photo de profil</Label>
                    <ImageUpload
                      currentImage={profile.profile_image}
                      onImageUploaded={(url) => handlePhotoUpload(url, 'profile')}
                      type="profile"
                      size="lg"
                    />
                  </div>
                  <div className="flex-1">
                    <Label className="mb-2">Photo de couverture</Label>
                    <ImageUpload
                      currentImage={profile.cover_image}
                      onImageUploaded={(url) => handlePhotoUpload(url, 'cover')}
                      type="cover"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label>Titre</Label>
                    <Select value={profile.title} onValueChange={(v) => setProfile({ ...profile, title: v })}>
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="Titre" />
                      </SelectTrigger>
                      <SelectContent>
                        {titles.map(t => (
                          <SelectItem key={t} value={t}>{t}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="md:col-span-2">
                    <Label>Spécialité *</Label>
                    <Select value={profile.specialty} onValueChange={(v) => setProfile({ ...profile, specialty: v })}>
                      <SelectTrigger className="mt-1" data-testid="specialty-select">
                        <SelectValue placeholder="Choisir votre spécialité" />
                      </SelectTrigger>
                      <SelectContent>
                        {specialties.map(s => (
                          <SelectItem key={s.id} value={s.name}>{s.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label>À propos de vous (Bio)</Label>
                  <Textarea
                    placeholder="Décrivez votre parcours, votre approche, vos domaines d'expertise..."
                    value={profile.bio || ''}
                    onChange={(e) => setProfile({ ...profile, bio: e.target.value })}
                    className="mt-1"
                    rows={4}
                    data-testid="bio-textarea"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Téléphone</Label>
                    <div className="relative mt-1">
                      <Phone className="absolute left-3 top-3 w-4 h-4 text-slate-400" />
                      <Input
                        placeholder="+237 6XX XXX XXX"
                        value={profile.phone || ''}
                        onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
                        className="pl-10"
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Email professionnel</Label>
                    <div className="relative mt-1">
                      <Mail className="absolute left-3 top-3 w-4 h-4 text-slate-400" />
                      <Input
                        type="email"
                        placeholder="contact@cabinet.cm"
                        value={profile.email || ''}
                        onChange={(e) => setProfile({ ...profile, email: e.target.value })}
                        className="pl-10"
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <Label>Site web</Label>
                  <div className="relative mt-1">
                    <Globe className="absolute left-3 top-3 w-4 h-4 text-slate-400" />
                    <Input
                      placeholder="https://www.moncabinet.cm"
                      value={profile.website || ''}
                      onChange={(e) => setProfile({ ...profile, website: e.target.value })}
                      className="pl-10"
                    />
                  </div>
                </div>

                <div>
                  <Label>Adresse du cabinet</Label>
                  <div className="flex gap-2 mt-1">
                    <div className="relative flex-1">
                      <MapPin className="absolute left-3 top-3 w-4 h-4 text-slate-400" />
                      <Input
                        placeholder="Votre adresse complète"
                        value={profile.address || ''}
                        onChange={(e) => setProfile({ ...profile, address: e.target.value })}
                        className="pl-10"
                      />
                    </div>
                    <Button type="button" variant="outline" onClick={getLocation}>
                      <MapPin className="w-4 h-4" />
                    </Button>
                  </div>
                  {profile.latitude && (
                    <p className="text-xs text-emerald-600 mt-1 flex items-center gap-1">
                      <CheckCircle className="w-3 h-3" /> GPS enregistré
                    </p>
                  )}
                </div>

                <div>
                  <Label>Ville *</Label>
                  <AddressAutocomplete
                    value={profile.city}
                    onChange={(value) => setProfile({ ...profile, city: value })}
                    onSelect={(location) => {
                      setProfile(prev => ({
                        ...prev,
                        city: location.display_name,
                        latitude: location.lat,
                        longitude: location.lon
                      }));
                    }}
                    placeholder="Rechercher une ville en Afrique..."
                    filterType="cities"
                    showMap={profile.latitude !== null && profile.latitude !== undefined}
                    mapHeight="160px"
                    className="mt-1"
                    data-testid="city-autocomplete"
                  />
                  <p className="text-xs text-slate-500 mt-1">Commencez à taper pour voir les suggestions</p>
                </div>

                {/* Languages */}
                <div>
                  <Label>Langues parlées</Label>
                  <div className="flex gap-2 mt-1">
                    <Select value={newLanguage} onValueChange={setNewLanguage}>
                      <SelectTrigger className="flex-1">
                        <SelectValue placeholder="Ajouter une langue" />
                      </SelectTrigger>
                      <SelectContent>
                        {languageOptions.filter(l => !profile.languages.includes(l)).map(l => (
                          <SelectItem key={l} value={l}>{l}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => addToList('languages', newLanguage, setNewLanguage)}
                      disabled={!newLanguage}
                    >
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {profile.languages.map((lang, idx) => (
                      <Badge key={idx} variant="secondary" className="flex items-center gap-1">
                        <Languages className="w-3 h-3" />
                        {lang}
                        <X className="w-3 h-3 cursor-pointer hover:text-red-500" onClick={() => removeFromList('languages', idx)} />
                      </Badge>
                    ))}
                  </div>
                </div>
              </TabsContent>

              {/* Tab 2: Qualifications */}
              <TabsContent value="qualifications" className="mt-0 space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Années d&apos;expérience</Label>
                    <Input
                      type="number"
                      placeholder="Ex: 10"
                      value={profile.experience_years || ''}
                      onChange={(e) => setProfile({ ...profile, experience_years: e.target.value })}
                      className="mt-1"
                      min="0"
                    />
                  </div>
                  <div>
                    <Label>Numéro de licence / Ordre</Label>
                    <Input
                      placeholder="Ex: MED-123456"
                      value={profile.license_number || ''}
                      onChange={(e) => setProfile({ ...profile, license_number: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                </div>

                <div>
                  <Label>Formation / Diplômes</Label>
                  <Textarea
                    placeholder="Ex: Doctorat en médecine - Université de Yaoundé I (2010)&#10;Spécialisation en cardiologie - CHU Yaoundé (2015)"
                    value={profile.education || ''}
                    onChange={(e) => setProfile({ ...profile, education: e.target.value })}
                    className="mt-1"
                    rows={3}
                  />
                </div>

                {/* Certifications */}
                <div>
                  <Label className="flex items-center gap-2">
                    <Award className="w-4 h-4" />
                    Certifications & Formations complémentaires
                  </Label>
                  <div className="flex gap-2 mt-1">
                    <Input
                      placeholder="Ex: Certification en échographie cardiaque"
                      value={newCertification}
                      onChange={(e) => setNewCertification(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addToList('certifications', newCertification, setNewCertification))}
                    />
                    <Button type="button" variant="outline" onClick={() => addToList('certifications', newCertification, setNewCertification)}>
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {profile.certifications?.map((cert, idx) => (
                      <Badge key={idx} variant="outline" className="flex items-center gap-1">
                        <Award className="w-3 h-3" />
                        {cert}
                        <X className="w-3 h-3 cursor-pointer hover:text-red-500" onClick={() => removeFromList('certifications', idx)} />
                      </Badge>
                    ))}
                  </div>
                </div>

                {/* Affiliations */}
                <div>
                  <Label className="flex items-center gap-2">
                    <Building2 className="w-4 h-4" />
                    Affiliations (Hôpitaux, Cliniques)
                  </Label>
                  <div className="flex gap-2 mt-1">
                    <Input
                      placeholder="Ex: Hôpital Central de Yaoundé"
                      value={newAffiliation}
                      onChange={(e) => setNewAffiliation(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addToList('affiliations', newAffiliation, setNewAffiliation))}
                    />
                    <Button type="button" variant="outline" onClick={() => addToList('affiliations', newAffiliation, setNewAffiliation)}>
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {profile.affiliations?.map((aff, idx) => (
                      <Badge key={idx} className="bg-blue-100 text-blue-800 flex items-center gap-1">
                        <Building2 className="w-3 h-3" />
                        {aff}
                        <X className="w-3 h-3 cursor-pointer hover:text-red-500" onClick={() => removeFromList('affiliations', idx)} />
                      </Badge>
                    ))}
                  </div>
                </div>

                {/* Achievements */}
                <div>
                  <Label className="flex items-center gap-2">
                    <Award className="w-4 h-4" />
                    Distinctions & Prix
                  </Label>
                  <div className="flex gap-2 mt-1">
                    <Input
                      placeholder="Ex: Prix d'excellence médicale 2020"
                      value={newAchievement}
                      onChange={(e) => setNewAchievement(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addToList('achievements', newAchievement, setNewAchievement))}
                    />
                    <Button type="button" variant="outline" onClick={() => addToList('achievements', newAchievement, setNewAchievement)}>
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {profile.achievements?.map((ach, idx) => (
                      <Badge key={idx} className="bg-amber-100 text-amber-800 flex items-center gap-1">
                        {ach}
                        <X className="w-3 h-3 cursor-pointer hover:text-red-500" onClick={() => removeFromList('achievements', idx)} />
                      </Badge>
                    ))}
                  </div>
                </div>
              </TabsContent>

              {/* Tab 3: Services */}
              <TabsContent value="services" className="mt-0 space-y-6">
                <div>
                  <Label className="text-base font-semibold">Types de consultations proposées</Label>
                  <div className="space-y-3 mt-3">
                    <label className={`flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition-colors ${profile.services.includes('consultation') ? 'border-red-500 bg-red-50' : 'border-slate-200 hover:border-red-200'}`}>
                      <input
                        type="checkbox"
                        checked={profile.services.includes('consultation')}
                        onChange={() => handleServiceToggle('consultation')}
                        className="sr-only"
                      />
                      <Calendar className="w-6 h-6 text-red-500" />
                      <div className="flex-1">
                        <span className="font-medium">Consultation sur place</span>
                        <p className="text-sm text-slate-500">Les patients viennent à votre cabinet</p>
                      </div>
                      {profile.services.includes('consultation') && <CheckCircle className="w-5 h-5 text-red-500" />}
                    </label>

                    <label className={`flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition-colors ${profile.services.includes('video_call') ? 'border-purple-500 bg-purple-50' : 'border-slate-200 hover:border-purple-200'}`}>
                      <input
                        type="checkbox"
                        checked={profile.services.includes('video_call')}
                        onChange={() => handleServiceToggle('video_call')}
                        className="sr-only"
                      />
                      <Video className="w-6 h-6 text-purple-600" />
                      <div className="flex-1">
                        <span className="font-medium">Téléconsultation</span>
                        <p className="text-sm text-slate-500">Consultations vidéo à distance</p>
                      </div>
                      {profile.services.includes('video_call') && <CheckCircle className="w-5 h-5 text-purple-500" />}
                    </label>

                    <label className={`flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition-colors ${profile.services.includes('home_visit') ? 'border-amber-500 bg-amber-50' : 'border-slate-200 hover:border-amber-200'}`}>
                      <input
                        type="checkbox"
                        checked={profile.services.includes('home_visit')}
                        onChange={() => handleServiceToggle('home_visit')}
                        className="sr-only"
                      />
                      <Home className="w-6 h-6 text-amber-600" />
                      <div className="flex-1">
                        <span className="font-medium">Visite à domicile</span>
                        <p className="text-sm text-slate-500">Vous vous déplacez chez le patient</p>
                      </div>
                      {profile.services.includes('home_visit') && <CheckCircle className="w-5 h-5 text-amber-500" />}
                    </label>
                  </div>
                </div>

                {/* Home visit settings */}
                {profile.services.includes('home_visit') && (
                  <div className="bg-amber-50 rounded-xl p-4 space-y-4">
                    <Label className="font-semibold flex items-center gap-2">
                      <Home className="w-4 h-4" />
                      Paramètres visites à domicile
                    </Label>
                    
                    <div>
                      <Label className="text-sm">Rayon de déplacement (km)</Label>
                      <Input
                        type="number"
                        value={profile.home_visit_radius_km || 10}
                        onChange={(e) => setProfile({ ...profile, home_visit_radius_km: e.target.value })}
                        className="mt-1 w-32"
                        min="1"
                        max="100"
                      />
                    </div>
                    
                    <div>
                      <Label className="text-sm">Zones couvertes</Label>
                      <div className="flex gap-2 mt-1">
                        <AddressAutocomplete
                          value={newArea}
                          onChange={(value) => setNewArea(value)}
                          onSelect={(location) => {
                            // Extract just the city/neighborhood name
                            const areaName = location.address?.suburb || location.address?.neighbourhood ||
                                           location.address?.city || location.address?.town || 
                                           location.address?.village || location.display_name.split(',')[0];
                            if (areaName && !profile.home_visit_areas.includes(areaName)) {
                              setProfile(prev => ({
                                ...prev,
                                home_visit_areas: [...prev.home_visit_areas, areaName]
                              }));
                            }
                            setNewArea('');
                          }}
                          placeholder="Rechercher un quartier ou une ville..."
                          filterType="all"
                          className="flex-1"
                          data-testid="covered-area-autocomplete"
                        />
                        <Button 
                          type="button" 
                          variant="outline" 
                          onClick={() => {
                            if (newArea.trim() && !profile.home_visit_areas.includes(newArea.trim())) {
                              addToList('home_visit_areas', newArea, setNewArea);
                            }
                          }}
                        >
                          <Plus className="w-4 h-4" />
                        </Button>
                      </div>
                      <p className="text-xs text-slate-500 mt-1">Sélectionnez dans les suggestions ou tapez manuellement</p>
                      <div className="flex flex-wrap gap-2 mt-2">
                        {profile.home_visit_areas?.map((area, idx) => (
                          <Badge key={idx} variant="outline" className="flex items-center gap-1">
                            <MapPin className="w-3 h-3" />
                            {area}
                            <X className="w-3 h-3 cursor-pointer hover:text-red-500" onClick={() => removeFromList('home_visit_areas', idx)} />
                          </Badge>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* Insurance */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <Label className="font-semibold">Accepte les assurances maladie</Label>
                    <Switch
                      checked={profile.accepts_insurance}
                      onCheckedChange={(v) => setProfile({ ...profile, accepts_insurance: v })}
                    />
                  </div>
                  
                  {profile.accepts_insurance && (
                    <div>
                      <Label className="text-sm">Assurances acceptées</Label>
                      <div className="flex gap-2 mt-1">
                        <Input
                          placeholder="Ex: CNPS, AXA, Allianz..."
                          value={newInsurance}
                          onChange={(e) => setNewInsurance(e.target.value)}
                          onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addToList('insurance_providers', newInsurance, setNewInsurance))}
                        />
                        <Button type="button" variant="outline" onClick={() => addToList('insurance_providers', newInsurance, setNewInsurance)}>
                          <Plus className="w-4 h-4" />
                        </Button>
                      </div>
                      <div className="flex flex-wrap gap-2 mt-2">
                        {profile.insurance_providers?.map((ins, idx) => (
                          <Badge key={idx} className="bg-green-100 text-green-800 flex items-center gap-1">
                            <Shield className="w-3 h-3" />
                            {ins}
                            <X className="w-3 h-3 cursor-pointer hover:text-red-500" onClick={() => removeFromList('insurance_providers', idx)} />
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex items-center justify-between">
                  <Label className="font-semibold">Accepte les nouveaux patients</Label>
                  <Switch
                    checked={profile.accepts_new_patients}
                    onCheckedChange={(v) => setProfile({ ...profile, accepts_new_patients: v })}
                  />
                </div>
              </TabsContent>

              {/* Tab 4: Availability */}
              <TabsContent value="availability" className="mt-0 space-y-6">
                <div className="bg-blue-50 rounded-lg p-4 text-sm text-blue-800">
                  <p>Définissez vos plages horaires disponibles pour chaque type de consultation. Les patients ne pourront réserver que sur ces créneaux.</p>
                </div>

                {/* Consultation slots */}
                {profile.services.includes('consultation') && (
                  <div>
                    <Label className="text-base font-semibold flex items-center gap-2 mb-4">
                      <Calendar className="w-5 h-5 text-red-500" />
                      Consultations sur place
                      <Badge variant="outline" className="ml-2">{profile.consultation_duration} min</Badge>
                    </Label>
                    
                    <div className="mb-4">
                      <Label className="text-sm">Durée d&#39;une consultation (minutes)</Label>
                      <Input
                        type="number"
                        value={profile.consultation_duration}
                        onChange={(e) => setProfile({ ...profile, consultation_duration: e.target.value })}
                        className="w-24 mt-1"
                        min="10"
                        max="120"
                        step="5"
                      />
                    </div>

                    <div className="space-y-3">
                      {Object.entries(dayLabels).map(([day, label]) => (
                        <div key={day} className="p-3 bg-slate-50 rounded-lg">
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-medium text-sm">{label}</span>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => addTimeSlot('consultation', day)}
                              className="text-xs h-7 px-2"
                            >
                              <Plus className="w-3 h-3 mr-1" /> Ajouter
                            </Button>
                          </div>
                          <div className="space-y-2">
                            {(profile.consultation_slots[day] || []).length === 0 ? (
                              <p className="text-xs text-slate-400 italic">Aucun créneau</p>
                            ) : (
                              (profile.consultation_slots[day] || []).map((slot, idx) => (
                                <div key={idx} className="flex items-center gap-2 flex-wrap sm:flex-nowrap">
                                  <Input
                                    type="time"
                                    value={slot.start}
                                    onChange={(e) => updateTimeSlot('consultation', day, idx, 'start', e.target.value)}
                                    className="w-[100px] sm:w-28 h-9 text-sm"
                                  />
                                  <span className="text-slate-400 text-sm">à</span>
                                  <Input
                                    type="time"
                                    value={slot.end}
                                    onChange={(e) => updateTimeSlot('consultation', day, idx, 'end', e.target.value)}
                                    className="w-[100px] sm:w-28 h-9 text-sm"
                                  />
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => removeTimeSlot('consultation', day, idx)}
                                    className="text-red-500 hover:text-red-700 h-8 w-8 p-0"
                                  >
                                    <X className="w-4 h-4" />
                                  </Button>
                                </div>
                              ))
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Video slots */}
                {profile.services.includes('video_call') && (
                  <div className="mt-8">
                    <Label className="text-base font-semibold flex items-center gap-2 mb-4">
                      <Video className="w-5 h-5 text-purple-500" />
                      Téléconsultations
                      <Badge variant="outline" className="ml-2">{profile.video_duration} min</Badge>
                    </Label>
                    
                    <div className="mb-4">
                      <Label className="text-sm">Durée d&#39;une téléconsultation (minutes)</Label>
                      <Input
                        type="number"
                        value={profile.video_duration}
                        onChange={(e) => setProfile({ ...profile, video_duration: e.target.value })}
                        className="w-24 mt-1"
                        min="10"
                        max="60"
                        step="5"
                      />
                    </div>

                    <div className="space-y-3">
                      {Object.entries(dayLabels).map(([day, label]) => (
                        <div key={day} className="p-3 bg-purple-50 rounded-lg">
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-medium text-sm">{label}</span>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => addTimeSlot('video', day)}
                              className="text-xs h-7 px-2"
                            >
                              <Plus className="w-3 h-3 mr-1" /> Ajouter
                            </Button>
                          </div>
                          <div className="space-y-2">
                            {(profile.video_slots[day] || []).length === 0 ? (
                              <p className="text-xs text-slate-400 italic">Aucun créneau</p>
                            ) : (
                              (profile.video_slots[day] || []).map((slot, idx) => (
                                <div key={idx} className="flex items-center gap-2 flex-wrap sm:flex-nowrap">
                                  <Input
                                    type="time"
                                    value={slot.start}
                                    onChange={(e) => updateTimeSlot('video', day, idx, 'start', e.target.value)}
                                    className="w-[100px] sm:w-28 h-9 text-sm"
                                  />
                                  <span className="text-slate-400 text-sm">à</span>
                                  <Input
                                    type="time"
                                    value={slot.end}
                                    onChange={(e) => updateTimeSlot('video', day, idx, 'end', e.target.value)}
                                    className="w-[100px] sm:w-28 h-9 text-sm"
                                  />
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => removeTimeSlot('video', day, idx)}
                                    className="text-red-500 hover:text-red-700 h-8 w-8 p-0"
                                  >
                                    <X className="w-4 h-4" />
                                  </Button>
                                </div>
                              ))
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {!profile.services.includes('consultation') && !profile.services.includes('video_call') && (
                  <div className="text-center py-8 text-slate-500">
                    <Calendar className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>Activez d&#39;abord les services &quot;Consultation sur place&quot; ou &quot;Téléconsultation&quot; dans l&#39;onglet Services</p>
                  </div>
                )}
              </TabsContent>

              {/* Tab 5: Pricing */}
              <TabsContent value="pricing" className="mt-0 space-y-6">
                <div className="bg-slate-50 rounded-lg p-4 text-sm text-slate-600">
                  <p>Définissez vos tarifs pour chaque type de consultation. Ces montants seront affichés aux patients.</p>
                </div>

                {profile.services.includes('consultation') && (
                  <div className="flex items-center gap-4 p-4 bg-red-50 rounded-xl">
                    <Calendar className="w-8 h-8 text-red-500" />
                    <div className="flex-1">
                      <Label className="font-medium">Consultation sur place</Label>
                      <p className="text-sm text-slate-500">Durée: {profile.consultation_duration} minutes</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        value={profile.consultation_fee || ''}
                        onChange={(e) => setProfile({ ...profile, consultation_fee: e.target.value })}
                        placeholder="10000"
                        className="w-32 text-right"
                        data-testid="consultation-fee"
                      />
                      <span className="text-slate-500 font-medium">FCFA</span>
                    </div>
                  </div>
                )}

                {profile.services.includes('video_call') && (
                  <div className="flex items-center gap-4 p-4 bg-purple-50 rounded-xl">
                    <Video className="w-8 h-8 text-purple-500" />
                    <div className="flex-1">
                      <Label className="font-medium">Téléconsultation</Label>
                      <p className="text-sm text-slate-500">Durée: {profile.video_duration} minutes</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        value={profile.video_fee || ''}
                        onChange={(e) => setProfile({ ...profile, video_fee: e.target.value })}
                        placeholder="8000"
                        className="w-32 text-right"
                        data-testid="video-fee"
                      />
                      <span className="text-slate-500 font-medium">FCFA</span>
                    </div>
                  </div>
                )}

                {profile.services.includes('home_visit') && (
                  <div className="flex items-center gap-4 p-4 bg-amber-50 rounded-xl">
                    <Home className="w-8 h-8 text-amber-500" />
                    <div className="flex-1">
                      <Label className="font-medium">Visite à domicile</Label>
                      <p className="text-sm text-slate-500">Rayon: {profile.home_visit_radius_km} km</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        value={profile.home_visit_fee || ''}
                        onChange={(e) => setProfile({ ...profile, home_visit_fee: e.target.value })}
                        placeholder="15000"
                        className="w-32 text-right"
                        data-testid="home-fee"
                      />
                      <span className="text-slate-500 font-medium">FCFA</span>
                    </div>
                  </div>
                )}

                {profile.services.length === 0 && (
                  <div className="text-center py-8 text-slate-500">
                    <CreditCard className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>Activez d&#39;abord vos services dans l&#39;onglet Services</p>
                  </div>
                )}
              </TabsContent>
            </CardContent>
          </Card>

          {/* Save button at bottom */}
          <div className="flex justify-end mt-6">
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-red-500 hover:bg-red-600 text-white px-8"
              data-testid="save-profile-btn-bottom"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Enregistrer les modifications
            </Button>
          </div>
        </Tabs>
      </div>
    </div>
  );
};

export default ProfessionalProfile;
